/**
 * Privacy page. Plain statements of what is stored where, kept in step with the
 * code that decides it: src/services/syncCore.js (what an account syncs) and
 * supabase/schema.sql (what the server holds).
 */

import { el } from '../utils/dom.js';
import { linkButton, sectionHead } from '../components/ui.js';
import { isSupabaseConfigured } from '../lib/supabaseClient.js';

const UPDATED = '2 October 2026';

const list = (items) => el('ul.privacy__list', {}, items.map((text) => el('li', { text })));

function block(title, children) {
  return el('section.section', {}, [sectionHead(title), el('div.card.privacy__card', {}, children)]);
}

export function PrivacyView() {
  const accounts = isSupabaseConfigured();

  const element = el('div.privacy', {}, [
    el('div.page-head', {}, [
      el('div.page-head__text', {}, [
        el('h1', { text: 'Privacy' }),
        el('p', {
          text: 'QuantMaster works without an account. Unless you create one, everything you do stays in this browser on this device.'
        })
      ])
    ]),

    block('Without an account', [
      el('p', { text: 'Your progress, answer history and settings are saved in this browser’s local storage. None of it is sent to a server.' }),
      el('p.text-muted', {
        text: 'Like any website, the server that delivers the app keeps standard technical logs (IP address, time and the file requested) for security and troubleshooting. They are rotated automatically and are not used to track you.'
      })
    ]),

    accounts
      ? block('With an account (optional)', [
          el('p', { text: 'If you create an account, these are stored with Supabase, the database service we use:' }),
          list([
            'Your email address and a securely hashed password (never the password itself).',
            'Your progress totals: XP and rank, streak, overall statistics, mastery per concept, level and topic progress, formula-card review schedule and achievements.',
            'Short summaries of your last 60 sessions: date, session name, score and XP.'
          ]),
          el('p', { text: 'These never leave your device, even with an account:' }),
          list([
            'Which exact questions you were asked.',
            'What you answered, and your list of mistakes.',
            'Your detailed answer history.'
          ]),
          el('p', {
            text: 'If you use leagues, your nickname and which leagues you are in are stored too. Members of a league see only each other’s nickname, XP earned this week and current streak.'
          }),
          el('p', {
            text: 'If you switch on the daily reminder while signed in, this device’s push address (issued by your browser), your reminder time and your time zone are stored, so the reminder can reach you when the app is closed. Switching the reminder off removes them.'
          }),
          el('p.text-muted', {
            text: 'We send no emails at all: there is no confirmation email and no password reset, so keep your password safe. You can change it in Settings while signed in.'
          })
        ])
      : null,

    block('What we never do', [
      list([
        'No advertising, and no analytics or tracking scripts.',
        'No selling or sharing of your data.',
        'No cookies: the app keeps everything in your browser’s own storage instead.'
      ])
    ]),

    block('Deleting your data', [
      list([
        accounts
          ? 'Your account: Settings → Account → Delete account. It is deleted immediately, together with the progress synced to it.'
          : null,
        'This device: Settings → Reset all progress, or clear this site’s data in your browser settings.'
      ].filter(Boolean))
    ]),

    el('p.text-xs.text-subtle', { text: `Last updated ${UPDATED}.` }),
    linkButton('Back to settings', '/settings', { variant: 'ghost' })
  ]);

  return { title: 'Privacy', element };
}
